import type {
  SelectedPanel,
  SelectedRun,
} from '@wandb/forge-shell-contract/aria';

import type {ContentPart, ImagePart} from '../store';
import type {ComposerFile} from './file';
import {toImagePart} from './imageParts';

export interface AssemblePromptPartsInput {
  // Attached panel chips (ambient context), prepended before the message body.
  panels: SelectedPanel[];
  // Attached run chips (ambient context), prepended before the message body.
  runs: SelectedRun[];
  // The typed message; starts with a text part (empty when it leads with a mention).
  body: ContentPart[];
  // File attachments (images today), appended after the body.
  files: ComposerFile[];
}

/**
 * assemblePromptParts orders a user prompt from a draft's pieces. Panel and
 * run chips lead as ambient context, then the message body, then files after a text
 * anchor so they survive the `getVisiblePromptParts` slice (everything before
 * the first text part is treated as ambient and dropped from display).
 */
export function assemblePromptParts({
  panels,
  runs,
  body,
  files,
}: AssemblePromptPartsInput): ContentPart[] {
  const panelParts: ContentPart[] = panels.map(panel => ({
    type: 'wandb-workspace-panel',
    entityName: panel.entityName,
    projectName: panel.projectName,
    viewId: panel.viewId,
    viewType: panel.viewType,
    panelId: panel.panelId,
    panelType: panel.panelType,
    title: panel.title,
    queryPanelTableKey: panel.queryPanelTableKey,
    queryPanelTableKeyType: panel.queryPanelTableKeyType,
  }));
  const runParts: ContentPart[] = runs.map(run => ({
    type: 'wandb-run',
    entityName: run.entityName,
    projectName: run.projectName,
    runName: run.name,
    runDisplayName: run.displayName,
    runDisplayNameStatus: 'resolved',
  }));

  const parts: ContentPart[] = [...panelParts, ...runParts, ...body];
  if (files.length > 0) {
    if (!parts.some(part => part.type === 'text')) {
      parts.push({type: 'text', text: '', status: 'complete'});
    }

    // Drop client-only rendering and analytics fields from the wire prompt.
    parts.push(...files.map(toImagePart));
  }

  return parts;
}

export interface DisassembledPrompt {
  panels: SelectedPanel[];
  runs: SelectedRun[];
  body: ContentPart[];
  images: ImagePart[];
}

/**
 * disassemblePromptParts splits assembled parts back into a draft's pieces.
 * Images are restored as files. Structured references remain in the body so
 * they render as inline, removable mentions and remain visible after sending.
 * Legacy panel/run draft fields are returned empty.
 */
export function disassemblePromptParts(
  parts: ContentPart[]
): DisassembledPrompt {
  const body: ContentPart[] = [];
  const images: ImagePart[] = [];
  for (const part of parts) {
    if (isAttachmentPart(part)) {
      images.push(part);
    } else {
      body.push(part);
    }
  }

  return {panels: [], runs: [], body, images};
}

/**
 * isAttachmentPart reports whether a part round-trips through the composer as a
 * file rather than inline body content.
 */
function isAttachmentPart(part: ContentPart): part is ImagePart {
  switch (part.type) {
    case 'image':
      return true;
    case 'text':
    case 'thinking':
    case 'tool-call':
    case 'wandb-entity':
    case 'wandb-project':
    case 'wandb-run':
    case 'wandb-call':
    case 'wandb-conversation':
    case 'wandb-workspace-panel':
    case 'wandb-web-page-state':
    case 'wandb-artifact-collection':
    case 'unknown':
      return false;
    default: {
      // Force a compile error when a new ContentPart variant is added, so its
      // composer round-trip is classified here rather than silently dropped.
      const _exhaustive: never = part;
      return _exhaustive;
    }
  }
}
